import { raise } from '@sparkdotfi/common-universal'
import { useSuspenseQueries } from '@tanstack/react-query'
import { useConfig } from 'wagmi'

import { getChainConfigEntry } from '@/config/chain'
import { useTokenRepository } from '@/domain/token-repository/useTokenRepository'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { SavingsAccount, SavingsAccountRepository } from '@/features/savings/logic/savings-converters/types'
import { useTimestamp } from '@/utils/useTimestamp'

export interface UseSavingsAccountRepositoryParams {
  chainId: number
  timestamp?: number
}

export function useSavingsAccountRepository({
  chainId,
  timestamp: _timestamp,
}: UseSavingsAccountRepositoryParams): SavingsAccountRepository {
  const wagmiConfig = useConfig()
  const { timestamp: defaultTimestamp } = useTimestamp()
  const timestamp = _timestamp ?? defaultTimestamp
  const chainConfig = getChainConfigEntry(chainId)
  const savingsConfig = chainConfig.savings ?? raise('Savings config not available for this chain')
  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const tokenRepository = useTokenRepository({
    chainId,
    priceConfigs: savingsConfig.priceConfigs,
    account,
  })
  const fetchConverterQueries = savingsConfig.accounts.map(({ fetchConverterQuery }) => fetchConverterQuery)

  const converterQueries = useSuspenseQueries({
    queries: fetchConverterQueries.map((query) => ({
      ...query({
        wagmiConfig,
        chainId,
        timestamp,
      }),
    })),
  })

  const accounts: SavingsAccount[] = converterQueries
    .map(
      ({ data }, index) =>
        data && {
          converter: data,
          savingsToken: tokenRepository.findOneBySymbol(savingsConfig.accounts[index]!.savingsToken),
          underlyingToken: tokenRepository.findOneBySymbol(savingsConfig.accounts[index]!.underlyingToken),
          supportedAssets: savingsConfig.accounts[index]!.supportedAssets.map((symbol) =>
            tokenRepository.findOneBySymbol(symbol),
          ),
        },
    )
    .filter(Boolean)

  return new SavingsAccountRepository(accounts)
}
